import { JSX } from "react";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Separator } from "../ui/separator";
import { IndicatorNumber } from "./common";
import { CircleLoader } from "./loaders";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "lucide-react";

export type DropdownLinkConfig = {
  text: string;
  actionFn?: any;
  disabled?: boolean;
};

export type ActionButtonConfig = {
  text: string | JSX.Element;
  actionFn?: any;
  type?: "button" | "submit" | "reset" | undefined;
  variant?: "link" | "default" | "destructive" | "outline" | "secondary" | "ghost" | null | undefined;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  form?: string;
  size?: "default" | "xs" | "sm" | "lg" | "icon" | null | undefined
  dropdownLinks?: DropdownLinkConfig[]; // Add dropdown links if this button should be a dropdown
  icon?: JSX.Element;
};

interface Props {
  title: string;
  description?: string;
  actionButtons?: ActionButtonConfig[];
  breadcrumbs?: JSX.Element;
  showIndicator?: boolean;
  indicatorNumber?: number;
  className?: string;
  children?: JSX.Element;
  showBackButton?: boolean;
};

function FormGroupHeader(props: Props) {

  const { title, description, actionButtons, breadcrumbs, showIndicator, indicatorNumber, className, children, showBackButton } = props;
  const isServiceView = location.pathname.includes("services/");

  const navigate = useNavigate();

  return (
    <div className={cn("flex flex-col pt-2 mb-2 sticky top-0 bg-white z-40 select-none", className)}>
      {breadcrumbs && <div>{breadcrumbs}</div>}
      <div className="flex flex-wrap sm:flex-nowrap justify-between items-center space-y-2 gap-2">
        {
          showBackButton &&
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="mb-0">
            <ArrowLeftIcon className="size-4" />
          </Button>
        }
        <div className="mb-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">{title}</h2>
            {showIndicator && indicatorNumber && !isServiceView && (
              <IndicatorNumber number={indicatorNumber} />
            )}
          </div>
          {description && <p className="text-sm text-gray-500">{description}</p>}
        </div>
        <div className="flex gap-2 ml-auto">
          {
            children && (
              <div>
                {children}
              </div>
            )
          }
          {actionButtons && actionButtons.map((buttonConfig, index) => {
            const { form, className, actionFn, variant, disabled, text, type = "button", dropdownLinks, size, icon, loading } = buttonConfig;

            return dropdownLinks ? (
              <DropdownMenu key={`form-group-dropwdown-btn-${index}`}>
                <DropdownMenuTrigger asChild>
                  <Button className={className} variant={variant} disabled={disabled || loading}>
                    {icon && icon}
                    {text}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <div className="flex flex-col gap-2">
                    {dropdownLinks.map((link, linkIndex) => (
                      <DropdownMenuItem
                        key={linkIndex}
                        onClick={link.actionFn}
                        disabled={link.disabled || loading}
                        className="hover:cursor-pointer"
                      >
                        {link.text}
                      </DropdownMenuItem>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                form={form}
                className={className}
                key={`form-group-action-btn-${index}`}
                type={type}
                onClick={actionFn}
                variant={variant}
                disabled={disabled || loading}
                size={size}
              >
                <div className="flex items-center gap-2">
                  {loading
                    ? <CircleLoader visible={loading} height={16} width={16} strokeWidth={2} />
                    : icon && icon}
                  {text}
                </div>
              </Button>
            );
          })}
        </div>
      </div>
      <Separator className="mt-1" />
    </div>
  );
}

export default FormGroupHeader;
